import { useMemo } from 'react'
import Big from 'big.js'

import { VirtualFurnitureRoomType } from 'constants/virtualFurniture'
import type { ResolvedCatalogue } from 'models/purchaseFlowRevamp'
import { StagingType, type StagingDetailsDTO } from 'models/virtualStaging'

import { usePfStore, type PfStoreState } from './store'
import type { CartExtraSnapshot } from './slices/cartSlice'
import { makeConfigKey } from './slices/productConfigSlice'
import { buildStagingConfiguration } from './serializeLineConfiguration'
import { shootOutputLines } from './shootOutput'

/** Synthetic extra kind for the staging renovation row. */
export const STAGING_RENOVATION_KIND = 'STAGING_RENOVATION'

/**
 * Whether a staged photo is billed the renovation: objects removed, or a wall or floor change, on a photo
 * with a room to stage. A photo of an empty room needs both. Mirrors the backend's
 * `StagingRequestDTO.visualsWithBillableAddons`, which is what placement bills.
 */
export const isBillableStagingDetail = (detail: Omit<StagingDetailsDTO, 'productId'>): boolean => {
  const allRoomsEmpty = detail.roomStaging.every((room) => room.roomType === VirtualFurnitureRoomType.EMPTY_ROOM)
  const hasWallOrFloorChange = detail.type === StagingType.BKBN
    ? !!detail.wallPainting || !!detail.floor
    : detail.wallPainting != null || detail.floor != null
  const removesObjects = detail.removeObjects != null
  return allRoomsEmpty ? removesObjects && hasWallOrFloorChange : hasWallOrFloorChange || removesObjects
}

/** The catalogue fields the renovation reads; the shells tell a line made from the shoot (`shootOutputLines`). */
type RenovationCatalogue = Pick<ResolvedCatalogue, 'stagingRenovation'> & Partial<Pick<ResolvedCatalogue, 'shellProducts'>>

/**
 * Billable staged photos across every staging line in the cart. A line made from the shoot is configured
 * in the gallery after delivery, so rooms an earlier standalone edit left on it are not billed.
 */
export const billableStagingPhotoCount = (
  state: Pick<PfStoreState, 'items' | 'productConfigs'>,
  catalogue?: RenovationCatalogue
): number => {
  const fromShoot = shootOutputLines(state.items, catalogue?.shellProducts)
  return state.items.filter((item) => !fromShoot.has(item.id)).reduce((count, item) => {
    const details = buildStagingConfiguration(state.productConfigs[makeConfigKey(item.id, 'staging')])?.request.details ?? {}
    return count + Object.values(details).filter(isBillableStagingDetail).length
  }, 0)
}

/**
 * The renovation the order will bill (backend #666), as an order-level extra: quoted in the cart and
 * sent on the payment intent as one line. Placement derives it itself, so it is never sent on the order.
 * Null when nothing is billable or the catalogue offers no renovation.
 */
export const stagingRenovationExtra = (
  state: Pick<PfStoreState, 'items' | 'productConfigs'>,
  catalogue: RenovationCatalogue | undefined
): CartExtraSnapshot | null => {
  const renovation = catalogue?.stagingRenovation
  if (!renovation) return null
  const count = billableStagingPhotoCount(state, catalogue)
  if (count === 0) return null
  return {
    kind: STAGING_RENOVATION_KIND,
    unitPrice: new Big(renovation.price.amount).times(count).toFixed(2),
    labelKey: 'purchase_flow_revamp:cart.staging_renovation',
    productCode: renovation.productCode,
    count,
  }
}

/** Every order-level charge: the instruction step's extras plus the staging renovation. */
export const orderExtras = (
  state: Pick<PfStoreState, 'items' | 'productConfigs' | 'extrasFromInstructions'>,
  catalogue: RenovationCatalogue | undefined
): CartExtraSnapshot[] => {
  const renovation = stagingRenovationExtra(state, catalogue)
  return renovation ? [...state.extrasFromInstructions, renovation] : state.extrasFromInstructions
}

/** `orderExtras`, kept live from the store. */
export const useOrderExtras = (catalogue: RenovationCatalogue | undefined): CartExtraSnapshot[] => {
  const items = usePfStore((s) => s.items)
  const productConfigs = usePfStore((s) => s.productConfigs)
  const extrasFromInstructions = usePfStore((s) => s.extrasFromInstructions)
  return useMemo(
    () => orderExtras({ items, productConfigs, extrasFromInstructions }, catalogue),
    [items, productConfigs, extrasFromInstructions, catalogue]
  )
}
